/**
 * QuestionEditor - Create/edit exam question with options.
 * Modern card-based design consistent with admin UI.
 */

import { useState, useCallback } from "react";
import {
  IconPlus,
  IconTrash,
  IconCheck,
  IconSave,
  IconAlertTriangle,
  IconAcademicCap,
} from "../../../../components/Icons.tsx";
import { haptic } from "../../../../lib/telegram.ts";
import { getErrorMessage } from "../../../../lib/graphql.ts";
import { useCreateQuestion, useUpdateQuestion } from "../hooks";
import type { AdminExamQuestion } from "../hooks";

// =============================================================================
// TYPES
// =============================================================================

interface QuestionEditorProps {
  examId: string;
  question: AdminExamQuestion | null;
  onSave: () => void;
  onCancel: () => void;
}

interface OptionInput {
  id: string;
  text: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function QuestionEditor({
  examId,
  question,
  onSave,
  onCancel,
}: QuestionEditorProps) {
  const isNew = !question;

  const { createQuestion, isLoading: isCreating } = useCreateQuestion();
  const { updateQuestion, isLoading: isUpdating } = useUpdateQuestion();

  const [text, setText] = useState(question?.text ?? "");
  const [options, setOptions] = useState<OptionInput[]>(
    question?.options?.length
      ? question.options.map((o) => ({ id: o.id, text: o.text }))
      : [
          { id: "a", text: "" },
          { id: "b", text: "" },
          { id: "c", text: "" },
          { id: "d", text: "" },
        ]
  );
  const [correctAnswer, setCorrectAnswer] = useState(question?.correctAnswer ?? "a");
  const [error, setError] = useState<string | null>(null);

  const handleAddOption = useCallback(() => {
    const nextId = String.fromCharCode(97 + options.length);
    setOptions((prev) => [...prev, { id: nextId, text: "" }]);
    haptic("light");
  }, [options.length]);

  const handleRemoveOption = useCallback(
    (id: string) => {
      if (options.length <= 2) return;
      setOptions((prev) => prev.filter((o) => o.id !== id));
      if (correctAnswer === id) {
        setCorrectAnswer(options[0].id === id ? options[1].id : options[0].id);
      }
      haptic("light");
    },
    [options, correctAnswer]
  );

  const handleOptionTextChange = useCallback((id: string, newText: string) => {
    setOptions((prev) =>
      prev.map((o) => (o.id === id ? { ...o, text: newText } : o))
    );
  }, []);

  const handleSave = useCallback(async () => {
    setError(null);

    if (!text.trim()) {
      setError("Введите текст вопроса");
      return;
    }

    const filledOptions = options.filter((o) => o.text.trim());
    if (filledOptions.length < 2) {
      setError("Добавьте минимум 2 варианта ответа");
      return;
    }

    if (!filledOptions.some((o) => o.id === correctAnswer)) {
      setError("Выберите правильный ответ из заполненных вариантов");
      return;
    }

    haptic("light");

    try {
      if (isNew) {
        await createQuestion({
          examId,
          text: text.trim(),
          type: "SINGLE_CHOICE",
          options: filledOptions.map((o) => ({ id: o.id, text: o.text.trim() })),
          correctAnswer,
        });
      } else if (question) {
        await updateQuestion(question.id, {
          text: text.trim(),
          options: filledOptions.map((o) => ({ id: o.id, text: o.text.trim() })),
          correctAnswer,
        });
      }
      haptic("success");
      onSave();
    } catch (err) {
      haptic("error");
      setError(getErrorMessage(err, "Не удалось сохранить"));
    }
  }, [
    text,
    options,
    correctAnswer,
    isNew,
    examId,
    question,
    createQuestion,
    updateQuestion,
    onSave,
  ]);

  const isSaving = isCreating || isUpdating;
  const currentCorrectText = options.find((o) => o.id === correctAnswer)?.text;

  return (
    <div className="pb-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
          <IconAcademicCap className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">
            {isNew ? "Новый вопрос" : "Редактирование"}
          </h1>
          <p className="text-[13px] text-slate-500">
            {isNew ? "Добавьте вопрос и варианты ответов" : "Измените вопрос или ответы"}
          </p>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 flex items-start gap-3">
          <IconAlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Question form card */}
      <section className="mb-6">
        <h2 className="text-[15px] font-semibold text-slate-900 mb-3">
          Текст вопроса
        </h2>
        <div className="card p-4">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Введите текст вопроса..."
            rows={4}
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-none bg-slate-50/50"
          />
        </div>
      </section>

      {/* Options card */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[15px] font-semibold text-slate-900">
            Варианты ответов
          </h2>
          <span className="text-[12px] text-slate-400">
            Нажмите на букву, чтобы выбрать правильный
          </span>
        </div>
        <div className="card overflow-hidden">
          <div className="divide-y divide-slate-100">
            {options.map((option) => (
              <div
                key={option.id}
                className="flex items-center gap-3 p-3 hover:bg-slate-50/50 transition-colors"
              >
                {/* Correct answer selector */}
                <button
                  type="button"
                  onClick={() => {
                    haptic("light");
                    setCorrectAnswer(option.id);
                  }}
                  className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                    correctAnswer === option.id
                      ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/30 scale-105"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200 active:scale-95"
                  }`}
                  title={
                    correctAnswer === option.id
                      ? "Правильный ответ"
                      : "Выбрать как правильный"
                  }
                >
                  {correctAnswer === option.id ? (
                    <IconCheck className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-bold uppercase">{option.id}</span>
                  )}
                </button>

                {/* Option input */}
                <input
                  type="text"
                  value={option.text}
                  onChange={(e) => handleOptionTextChange(option.id, e.target.value)}
                  placeholder={`Вариант ${option.id.toUpperCase()}`}
                  className={`flex-1 px-3 py-2.5 rounded-xl border text-[15px] transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 ${
                    correctAnswer === option.id && option.text
                      ? "border-emerald-200 bg-emerald-50/50"
                      : "border-slate-200 bg-white"
                  }`}
                />

                {/* Remove button */}
                {options.length > 2 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveOption(option.id)}
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors active:scale-95"
                  >
                    <IconTrash className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Add option button */}
          {options.length < 8 && (
            <div className="p-3 border-t border-slate-100">
              <button
                type="button"
                onClick={handleAddOption}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-50 text-slate-600 font-medium text-[13px] hover:bg-slate-100 active:scale-[0.99] transition-all"
              >
                <IconPlus className="w-4 h-4" />
                Добавить вариант
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Correct answer preview */}
      {currentCorrectText && (
        <div className="mb-6 p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200/50">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
              <IconCheck className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-medium text-emerald-600 uppercase tracking-wide mb-1">
                Правильный ответ
              </p>
              <p className="text-[15px] text-emerald-900 font-medium">
                {currentCorrectText}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="flex-1 px-4 py-3.5 rounded-xl bg-slate-100 text-slate-700 font-medium disabled:opacity-50 active:scale-[0.98] transition-transform"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || !text.trim()}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-blue-600 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-transform shadow-lg shadow-blue-600/25"
        >
          {isSaving ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <IconSave className="w-4 h-4" />
              {isNew ? "Создать" : "Сохранить"}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
